/**
 * Hook React para integración con el ratón 3D de 3DConnexion (SpaceMouse).
 *
 * Solo activo en entorno Electron.
 * Expone estado de conexión, disponibilidad, y suscripciones a eventos.
 *
 * @example
 * ```tsx
 * const { isAvailable, connectionState, connect, onMotion } = useSpaceMouse();
 *
 * // Suscripción a datos de movimiento
 * onMotion((data) => {
 *   socket.emit('spacemouse:motion', data);
 * });
 * ```
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useIsElectron } from './useElectron';
import {
  SpaceMouseService,
  SpaceMouseConnectionState,
  SpaceMouseMotionData,
  SpaceMouseButtonEvent,
  SpaceMouseConfig,
} from '../services/spacemouse';

interface UseSpaceMouseOptions extends SpaceMouseConfig {
  /**
   * Si es true, inicia el polling de disponibilidad automáticamente al montar.
   * @default true
   */
  startOnMount?: boolean;
}

interface UseSpaceMouseReturn {
  /** Indica si el driver 3DxWare está corriendo localmente */
  isAvailable: boolean;
  /** Estado actual de conexión con el ratón 3D */
  connectionState: SpaceMouseConnectionState;
  /** Indica si estamos en un entorno donde el SpaceMouse es posible (Electron) */
  isSupported: boolean;
  /** Conectar al ratón 3D manualmente */
  connect: () => Promise<void>;
  /** Desconectar */
  disconnect: () => void;
  /** Resetear la cámara virtual (si los deltas acumulan drift) */
  resetCamera: () => void;
  /** Último dato de movimiento recibido (null si no ha habido movimiento) */
  lastMotion: SpaceMouseMotionData | null;
  /**
   * Suscripción a eventos de movimiento.
   * Usar dentro de un useEffect o pasar un callback estable.
   */
  onMotion: (callback: (data: SpaceMouseMotionData) => void) => () => void;
  /**
   * Suscripción a eventos de botón.
   */
  onButton: (callback: (data: SpaceMouseButtonEvent) => void) => () => void;
}

export function useSpaceMouse(
  options: UseSpaceMouseOptions = {}
): UseSpaceMouseReturn {
  const { startOnMount = true, ...config } = options;
  const isElectron = useIsElectron();

  const [isAvailable, setIsAvailable] = useState(false);
  const [connectionState, setConnectionState] = useState<SpaceMouseConnectionState>('disconnected');
  const [lastMotion, setLastMotion] = useState<SpaceMouseMotionData | null>(null);

  // Ref al servicio para acceso estable
  const serviceRef = useRef<SpaceMouseService | null>(null);

  // Obtener/crear instancia del servicio
  const getService = useCallback((): SpaceMouseService => {
    if (!serviceRef.current) {
      serviceRef.current = SpaceMouseService.getInstance();
    }
    return serviceRef.current;
  }, []);

  // Configurar servicio cuando cambian las opciones
  useEffect(() => {
    if (!isElectron) return;

    const service = getService();
    service.configure(config);
  }, [isElectron, config.appName, config.pollInterval, config.autoConnect, config.debug]); // eslint-disable-line react-hooks/exhaustive-deps

  // Suscribirse a eventos del servicio
  useEffect(() => {
    if (!isElectron) return;

    const service = getService();

    const handleConnectionChange = (state: SpaceMouseConnectionState) => {
      setConnectionState(state);
    };

    const handleAvailabilityChange = (available: boolean) => {
      setIsAvailable(available);
    };

    const handleMotion = (data: SpaceMouseMotionData) => {
      setLastMotion(data);
    };

    service.on('connectionChange', handleConnectionChange);
    service.on('availabilityChange', handleAvailabilityChange);
    service.on('motion', handleMotion);

    // Sincronizar estado inicial
    setIsAvailable(service.isAvailable());
    setConnectionState(service.getConnectionState());

    // Iniciar polling si está configurado
    if (startOnMount) {
      service.startPolling();
    }

    return () => {
      service.off('connectionChange', handleConnectionChange);
      service.off('availabilityChange', handleAvailabilityChange);
      service.off('motion', handleMotion);
      service.stopPolling();
    };
  }, [isElectron, startOnMount, getService]);

  // Callbacks expuestos
  const connect = useCallback(async () => {
    if (!isElectron) {
      console.warn('[useSpaceMouse] Not in Electron environment');
      return;
    }
    await getService().connect();
  }, [isElectron, getService]);

  const disconnect = useCallback(() => {
    getService().disconnect();
  }, [getService]);

  const resetCamera = useCallback(() => {
    getService().resetCamera();
  }, [getService]);

  /**
   * Suscribirse a datos de movimiento. Retorna función de unsubscribe.
   */
  const onMotion = useCallback(
    (callback: (data: SpaceMouseMotionData) => void): (() => void) => {
      const service = getService();
      service.on('motion', callback);
      return () => service.off('motion', callback);
    },
    [getService]
  );

  /**
   * Suscribirse a eventos de botón. Retorna función de unsubscribe.
   */
  const onButton = useCallback(
    (callback: (data: SpaceMouseButtonEvent) => void): (() => void) => {
      const service = getService();
      service.on('button', callback);
      return () => service.off('button', callback);
    },
    [getService]
  );

  return {
    isAvailable,
    connectionState,
    isSupported: isElectron,
    connect,
    disconnect,
    resetCamera,
    lastMotion,
    onMotion,
    onButton,
  };
}
